<script setup>
import { computed } from 'vue';
import { useApiData } from '../../composables/useApiData';
import { useAccount } from 'dashboard/composables/useAccount';
import { Card } from 'dashboard/components/ui/card';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from 'dashboard/components/ui/avatar';

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
});

const { accountId } = useAccount();

const { data, loading, error } = useApiData(
  computed(() => props.widget),
  computed(() => accountId.value)
);

const title = computed(() => props.widget.props?.title || 'Activités récentes');

const activities = computed(() => {
  if (
    data.value?.mapped?.activities &&
    Array.isArray(data.value.mapped.activities)
  ) {
    return data.value.mapped.activities;
  }

  if (
    props.widget.props?.activities &&
    Array.isArray(props.widget.props.activities)
  ) {
    return props.widget.props.activities;
  }

  return [
    {
      type: 'message',
      title: 'Nouveau message reçu',
      description: 'Message de Jean Dupont',
      time: 'Il y a 5 min',
      icon: 'i-lucide-message-circle',
    },
    {
      type: 'update',
      title: 'Mise à jour du projet',
      description: 'Statut changé en "En cours"',
      time: 'Il y a 1h',
      icon: 'i-lucide-refresh-cw',
    },
    {
      type: 'file',
      title: 'Document ajouté',
      description: 'Devis_2024.pdf',
      time: 'Il y a 2h',
      icon: 'i-lucide-file-text',
    },
  ];
});

const formatDate = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays < 7) return `Il y a ${diffDays}j`;
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};
</script>

<template>
  <div class="h-full flex flex-col font-inter group">
    <div class="flex items-center gap-3 mb-6 pb-4 border-b border-n-weak">
      <div
        class="flex h-10 w-10 items-center justify-center rounded-xl bg-n-brand/10 border border-n-brand/20 shadow-sm group-hover:shadow-brand-sm transition-all duration-300"
      >
        <span class="i-lucide-activity h-5 w-5 text-n-brand" />
      </div>
      <div class="flex-1 min-w-0">
        <h3 class="text-sm font-bold text-n-slate-12 truncate tracking-tight">
          {{ title }}
        </h3>
        <p
          class="text-[10px] font-bold text-n-slate-10 uppercase tracking-widest mt-0.5"
        >
          Flux en temps réel
        </p>
      </div>
      <div class="flex items-center gap-2">
        <div
          v-if="loading"
          class="size-2 bg-n-brand rounded-full animate-pulse shadow-brand-sm"
        />
        <span
          v-if="error"
          class="i-lucide-alert-circle size-4 text-n-ruby-9 cursor-help"
          :title="error"
        />
      </div>
    </div>
    <div class="flex-1 overflow-auto -mx-1 px-1">
      <div
        v-if="loading && activities.length === 0"
        class="flex items-center justify-center py-16"
      >
        <div class="flex flex-col items-center gap-4">
          <div
            class="h-10 w-10 border-3 rounded-full animate-spin border-n-brand/10 border-t-n-brand shadow-sm"
          />
          <span
            class="text-[11px] font-bold text-n-slate-10 uppercase tracking-widest"
            >Récupération...</span>
        </div>
      </div>
      <div
        v-else-if="error && activities.length === 0"
        class="flex flex-col items-center justify-center py-16"
      >
        <div
          class="h-14 w-14 rounded-2xl bg-ruby-1/10 flex items-center justify-center mb-4 border border-n-ruby-6/20 shadow-sm"
        >
          <span class="i-lucide-alert-circle size-7 text-n-ruby-9" />
        </div>
        <span class="text-sm font-medium text-n-slate-11 text-center px-6">{{
          error
        }}</span>
      </div>
      <div
        v-else-if="activities.length === 0"
        class="flex flex-col items-center justify-center py-16 text-center"
      >
        <div
          class="h-16 w-16 rounded-2xl bg-n-alpha-2 flex items-center justify-center mb-5 border border-n-weak shadow-sm"
        >
          <span class="i-lucide-activity size-8 text-n-slate-10" />
        </div>
        <h4 class="text-sm font-bold text-n-slate-12 mb-1">Aucune activité</h4>
        <p class="text-xs font-medium text-n-slate-10">
          Les événements apparaîtront ici
        </p>
      </div>
      <div v-else class="space-y-3 pb-2 relative">
        <!-- Timeline vertical line -->
        <div
          class="absolute left-[21px] top-4 bottom-4 w-0.5 bg-n-weak opacity-50 z-0"
        />

        <div
          v-for="(activity, index) in activities"
          :key="index"
          class="relative z-10 flex items-start gap-4 p-3.5 rounded-xl transition-all duration-300 hover:border-n-brand/30 hover:shadow-md bg-white border-n-weak cursor-pointer"
        >
          <div
            class="h-11 w-11 flex items-center justify-center rounded-xl transition-all duration-300 shadow-sm border border-n-weak shrink-0"
            :class="[
              activity.type === 'message'
                ? 'bg-n-blue-2 text-n-blue-11 group-hover:bg-n-blue-3'
                : activity.type === 'update'
                  ? 'bg-n-amber-2 text-n-amber-11 group-hover:bg-n-amber-3'
                  : 'bg-n-teal-2 text-n-teal-11 group-hover:bg-n-teal-3',
            ]"
          >
            <span
              :class="activity.icon || 'i-lucide-activity'"
              class="text-xl group-hover:scale-110 transition-transform"
            />
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-start justify-between gap-3">
              <div class="flex-1 min-w-0">
                <div
                  class="text-sm font-bold text-n-slate-12 truncate transition-colors duration-300 group-hover:text-n-brand"
                >
                  {{ activity.title }}
                </div>
                <div
                  v-if="activity.description"
                  class="text-[11px] font-medium text-n-slate-10 mt-0.5 line-clamp-1 group-hover:text-n-slate-11"
                >
                  {{ activity.description }}
                </div>
              </div>
              <span
                class="text-[10px] font-bold text-n-slate-10 flex-shrink-0 whitespace-nowrap uppercase tracking-widest bg-n-alpha-2 px-2 py-0.5 rounded-md"
              >
                {{
                  activity.time ||
                  formatDate(activity.created_at || activity.timestamp)
                }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
